import * as THREE from 'three'
import type { PerspectiveCamera, OrthographicCamera } from 'three'

import * as React from 'react'
import { forwardRef, useMemo, useEffect, useState } from 'react'
import { extend, useFrame, useThree, ReactThreeFiber, EventManager } from '@react-three/fiber'

import CameraControlsImpl from 'camera-controls'
import CameraControls from 'camera-controls'

export type CameraControlsProps = Omit<
  ReactThreeFiber.Overwrite<
    ReactThreeFiber.Node<CameraControlsImpl, typeof CameraControlsImpl>,
    {
      camera?: PerspectiveCamera | OrthographicCamera
      domElement?: HTMLElement
      makeDefault?: boolean
      onStart?: (e?: { type: 'controlstart' }) => void
      onEnd?: (e?: { type: 'controlend' }) => void
      onChange?: (e?: { type: 'update' }) => void
      onControl?: (e?: { type: 'control' }) => void
      events?: boolean // Wether to enable events during controls interaction
      regress?: boolean
    }
  >,
  'ref'
>

export const Camera2DControls = forwardRef<CameraControlsImpl, CameraControlsProps>((props, ref) => {
  useMemo(() => {
    CameraControlsImpl.install({ THREE })
    extend({ CameraControlsImpl })
  }, [])

  const { camera, domElement, makeDefault, onStart, onEnd, onChange, onControl, regress, ...restProps } = props

  const defaultCamera = useThree((state) => state.camera)
  const gl = useThree((state) => state.gl)
  const invalidate = useThree((state) => state.invalidate)
  const events = useThree((state) => state.events) as EventManager<HTMLElement>
  const setEvents = useThree((state) => state.events)
  const set = useThree((state) => state.set)
  const get = useThree((state) => state.get)
  const performance = useThree((state) => state.performance)
  const explCamera = camera || defaultCamera
  const explDomElement = (domElement || events.connected || gl.domElement) as HTMLElement
  if (camera === undefined) {
        explCamera.position.set(0, 0, 100)
        explCamera.up.set(0, 1, 0)       
        explCamera.lookAt(0, 0, 0)
  }

  const controls = useMemo(() => new CameraControlsImpl(explCamera), [explCamera]);

  useFrame((state, delta) => {
    // console.log(state.camera.position.x / 16)
    if (controls.enabled) {
      controls.update(delta)
    }
  }, -1)

  useEffect(() => {
    controls.connect(explDomElement)
    controls.truck(-1, 0)
    return () => void controls.disconnect()
  }, [explDomElement, controls])

  React.useEffect(() => {
    const callback = (e: { type: "update" } | undefined) => {
      invalidate()
      if (regress) performance.regress()
      if (onChange) onChange(e)
    }

    const onStartCb: CameraControlsProps['onStart'] = (e) => {
      if (onStart) onStart(e)
    }

    const onEndCb: CameraControlsProps['onEnd'] = (e) => {
      if (onEnd) onEnd(e)
    }

    controls.addEventListener('update', callback)
    controls.addEventListener('controlstart', onStartCb)
    controls.addEventListener('controlend', onEndCb)

    return () => {
      controls.removeEventListener('update', callback)
      controls.removeEventListener('controlstart', onStartCb)
      controls.removeEventListener('controlend', onEndCb)
    }
  }, [controls, onStart, onEnd, invalidate, setEvents, regress, onChange])

  useEffect(() => {
    if (makeDefault) {
      const old = get().controls
      set({ controls: controls as unknown as THREE.EventDispatcher })
      return () => set({ controls: old })
    }
  }, [makeDefault, controls]);

  controls.mouseButtons.left = CameraControls.ACTION.TRUCK;
  controls.mouseButtons.middle = CameraControls.ACTION.ZOOM;
  controls.mouseButtons.right = CameraControls.ACTION.TRUCK;
  controls.touches.one = CameraControls.ACTION.TOUCH_TRUCK;
  controls.touches.two = CameraControls.ACTION.TOUCH_ZOOM;

  // controls.disconnect();

  return <primitive ref={ref} object={controls} {...restProps} />
})

export type Camera2DControls = CameraControlsImpl